import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchDeletedPlugins, restoreDeletedPlugin } from '../../Utils/adminSettingsApi';

export default function DeletedPluginsPanel({ refreshKey = 0, onRestored }) {
  const { t, i18n } = useTranslation();
  const [plugins, setPlugins] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const generation = useRef(0);
  const load = useCallback(async (before = null) => {
    const request = ++generation.current;
    setLoading(true);
    setError('');
    try {
      const result = await fetchDeletedPlugins(before);
      if (request !== generation.current) return;
      setPlugins(current => before === null ? result.plugins : [...current, ...result.plugins]);
      setNext(result.next_before);
    } catch (_) {
      if (request === generation.current) setError('load_error');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load, refreshKey]);
  const restore = async plugin => {
    if (restoring || loading) return;
    setRestoring(plugin.folderName);
    setError('');
    setSuccess('');
    try {
      await restoreDeletedPlugin(plugin.folderName);
      setSuccess(plugin.name);
      await load();
      onRestored?.();
    } catch (_) { setError('restore_error'); }
    finally { setRestoring(''); }
  };
  return <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>{t('global_setting.deleted_plugins.title')}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('global_setting.deleted_plugins.help')}</Typography>
    {success && <Alert severity="success" role="status" sx={{ mb: 1 }}>{t('global_setting.deleted_plugins.success', { name: success })}</Alert>}
    {error && <Alert severity="error" sx={{ mb: 1 }} action={<Button disabled={!!restoring || loading} onClick={() => load()}>
      {t('dag.history.refresh')}</Button>}>{t(`global_setting.deleted_plugins.${error}`)}</Alert>}
    {!loading && !error && !plugins.length && <Typography variant="body2" color="text.secondary">{t('global_setting.deleted_plugins.empty')}</Typography>}
    <Stack component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }} spacing={2}>
      {plugins.map(plugin => <Stack key={plugin.id} component="li" direction={{ xs: 'column', sm: 'row' }}
        spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{plugin.name}</Typography>
          <Typography variant="body2" color="text.secondary">{t('global_setting.deleted_plugins.deleted_by', {
            name: plugin.authorName || t('dag.history.author_not_recorded'),
            date: new Date(plugin.deletedAt).toLocaleString(i18n.resolvedLanguage || i18n.language),
          })}</Typography>
        </Box>
        <Button variant="outlined" disabled={loading || !!restoring} onClick={() => restore(plugin)}
          aria-label={t('global_setting.deleted_plugins.restore_named', { name: plugin.name })}>
          {t(`global_setting.deleted_plugins.${restoring === plugin.folderName ? 'restoring' : 'restore'}`)}
        </Button>
      </Stack>)}
    </Stack>
    {loading && <Stack direction="row" alignItems="center" spacing={1} role="status" sx={{ mt: 1 }}>
      <CircularProgress size={18} /><Typography variant="body2">{t('dag.history.loading')}</Typography>
    </Stack>}
    {next && <Button disabled={loading || !!restoring} onClick={() => load(next)} sx={{ mt: 1 }}>{t('dag.history.more')}</Button>}
  </Box>;
}
